import { z } from 'zod';
import type { ProductVariant, ProductAttribute, Money, Quantity } from '../shared/types';
import { ProductVariantSchema, EntityBase, EntityType } from '../shared/types';

const VERSION_ONE = 1;

export class ProductVariantEntity {
  private constructor(private readonly props: ProductVariant) {}

  static create(props: Omit<ProductVariant, keyof z.infer<typeof EntityBase>>): ProductVariantEntity {
    const now = new Date();
    const variant = ProductVariantSchema.parse({
      ...props,
      _id: EntityType.enum.product_variant + globalThis.crypto.randomUUID(),
      type: EntityType.enum.product_variant,
      version: VERSION_ONE,
      createdAt: now,
      updatedAt: now,
    });
    return new ProductVariantEntity(variant);
  }

  static restore(props: ProductVariant): ProductVariantEntity {
    const variant = ProductVariantSchema.parse(props);
    return new ProductVariantEntity(variant);
  }

  get _id(): string {
    return this.props._id;
  }

  get productId(): string {
    return this.props.productId;
  }

  get sku(): string {
    return this.props.sku;
  }

  get barcode(): string | undefined {
    return this.props.barcode;
  }

  get attributes(): ProductAttribute[] {
    return [...this.props.attributes];
  }

  get price(): Money {
    return this.props.price;
  }

  get cost(): Money {
    return this.props.cost;
  }

  get isActive(): boolean {
    return this.props.isActive;
  }

  get version(): number {
    return this.props.version;
  }

  get type(): string {
    return this.props.type;
  }

  get minQuantity(): Quantity {
    return this.props.minQuantity;
  }

  get maxQuantity(): Quantity | undefined {
    return this.props.maxQuantity;
  }

  get createdAt(): Date {
    return this.props.createdAt;
  }

  get updatedAt(): Date {
    return this.props.updatedAt;
  }

  hasAttribute(name: string, value: string): boolean {
    return this.props.attributes.some(attr =>
      attr.name === name && attr.value === value
    );
  }

  matchesAttributes(attributes: ProductAttribute[]): boolean {
    return attributes.every(attr => this.hasAttribute(attr.name, attr.value));
  }

  updatePrice(price: Money): void {
    this.props.price = price;
    this.props.updatedAt = new Date();
  }

  updateCost(cost: Money): void {
    this.props.cost = cost;
    this.props.updatedAt = new Date();
  }

  deactivate(): void {
    this.props.isActive = false;
    this.props.updatedAt = new Date();
  }

  toJSON(): ProductVariant {
    return { ...this.props };
  }
}
